/**
 * Journal Page Component
 * Main page for trade journaling functionality
 */

import { toastError } from "../../../utils/showApiErrorToast";
import {
  Flex,
  Box,
  Button,
  useColorModeValue,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
} from "@chakra-ui/react";
import { toast } from "sonner";
import React, { useState, useMemo, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
// Components
import JournalEntryCard from "./components/JournalEntryCard";
import JournalEntryModal from "./components/JournalEntryModal";
import JournalStatistics from "./components/JournalStatistics";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import DateRangePicker from "../../../components/DateRangePicker/DateRangePicker";
// Types
import { JournalEntry, JournalEntryType, JournalFilters } from "../../../types/journal";
import { Trade } from "../../../types/trade";
// Services
import {
  getJournalEntries,
  saveJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  filterJournalEntries,
  getJournalEntriesByDate,
  getJournalEntriesByWeek,
} from "../../../services/journalService";
import { fetchTrades } from "../../../services/tradeApi";
import { backendTradeToFrontendTrade } from "../../../services/tradeStorage";
// Icons
import { AiOutlinePlus, AiOutlineSearch } from "react-icons/ai";

const Journal: React.FC = () => {
  const { user } = useUser();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [filters, setFilters] = useState<JournalFilters>({
    type: "all",
    searchQuery: "",
  });
  const [viewMode, setViewMode] = useState<"list" | "daily" | "weekly">("list");
  const bgColor = useColorModeValue("white", "#151517");
  const textColor = useColorModeValue("#575757", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const selectBg = useColorModeValue("white", "#151517");
  const menuBg = useColorModeValue("white", "#1A202C");
  const menuHoverBg = useColorModeValue("gray.50", "gray.700");

  // Load journal entries and trades
  useEffect(() => {
    if (user?.id) {
      loadEntries();
      loadTrades();
    }
  }, [user?.id]);

  // Listen for trade updates
  useEffect(() => {
    if (!user?.id) return;

    const handleTradesUpdate = (event: Event) => {
      const action = (event as CustomEvent<{ action?: string }>)?.detail?.action;
      if (action === "marketDataPreference") return;
      loadTrades();
    };

    window.addEventListener("tradesUpdated", handleTradesUpdate);
    window.addEventListener("storage", handleTradesUpdate);

    return () => {
      window.removeEventListener("tradesUpdated", handleTradesUpdate);
      window.removeEventListener("storage", handleTradesUpdate);
    };
  }, [user?.id]);

  const loadTrades = async () => {
    if (!user?.id) return;
    try {
      const response = await fetchTrades(); // No limit - fetch all trades
      const frontendTrades = response.trades.map(backendTradeToFrontendTrade);
      setTrades(frontendTrades);
    } catch (error) {
      console.error("Error loading trades from backend:", error);
      setTrades([]);
    }
  };

  // Apply filters
  useEffect(() => {
    if (user?.id) {
      const filtered = filterJournalEntries(entries, filters);
      setFilteredEntries(filtered);
    }
  }, [entries, filters, user?.id]);

  const loadEntries = () => {
    if (!user?.id) return;
    const userEntries = getJournalEntries(user.id);
    // Sort by date (newest first)
    const sorted = userEntries.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    setEntries(sorted);
  };

  const handleCreateEntry = () => {
    setSelectedEntry(null);
    setIsModalOpen(true);
  };

  const handleEditEntry = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleDeleteEntry = (entry: JournalEntry) => {
    if (!user?.id) return;

    if (window.confirm(`Are you sure you want to delete "${entry.title}"?`)) {
      const success = deleteJournalEntry(user.id, entry.id);
      if (success) {
        toast.success("Entry deleted", { description: "Journal entry has been deleted." });
        loadEntries();
      } else {
        toastError("Error", { description: "Failed to delete journal entry." });
      }
    }
  };

  const handleSaveEntry = (entryData: any) => {
    if (!user?.id) return;

    try {
      if (selectedEntry) {
        // Update existing entry
        const updated = updateJournalEntry(user.id, selectedEntry.id, entryData);
        if (updated) {
          toast.success("Entry updated", { description: "Journal entry has been updated." });
        }
      } else {
        // Create new entry
        saveJournalEntry(user.id, entryData);
        toast.success("Entry created", { description: "Journal entry has been created." });
      }
      loadEntries();
      setIsModalOpen(false);
      setSelectedEntry(null);
    } catch (error) {
      toastError("Error", { description: "Failed to save journal entry." });
    }
  };

  // Group entries by view mode
  const groupedEntries = useMemo(() => {
    if (viewMode === "daily") {
      return getJournalEntriesByDate(filteredEntries);
    } else if (viewMode === "weekly") {
      return getJournalEntriesByWeek(filteredEntries);
    }
    return null;
  }, [filteredEntries, viewMode]);

  const formatDate = (date: Date | string): string => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatWeekRange = (weekStart: string): string => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  return (
    <Flex direction='column' pt={{ base: "100px", md: "80px", lg: "40px" }}>
      <PageRouteHeadline mb="24px">Trade Journal</PageRouteHeadline>

      {/* Trading Psychology Statistics */}
      <JournalStatistics trades={trades} journalEntries={entries} />

      {/* Header with Button */}
      <Flex justify="flex-end" mb="24px">
        <Button
          bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
          color="white"
          leftIcon={<Icon as={AiOutlinePlus} />}
          onClick={handleCreateEntry}
          _hover={{
            bgGradient: "linear-gradient(135deg, #3EEAEC, #38BBD1)",
          }}
        >
          New Entry
        </Button>
      </Flex>

      {/* Filters */}
      <Box mb="24px">
        <HStack spacing={4} mb={4} flexWrap="wrap">
          <Select
            value={filters.type || "all"}
            onChange={(e) =>
              setFilters({ ...filters, type: e.target.value as JournalEntryType | "all" })
            }
            maxW="200px"
            bg={selectBg}
            border="none"
            color={textColor}
          >
            <option value="all">All Types</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="trade_review">Trade Review</option>
            <option value="reflection">Reflection</option>
          </Select>

          <Box flex="1" minW="200px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={AiOutlineSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search entries..."
                value={filters.searchQuery || ""}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                bg={bgColor}
                border="none"
              />
            </InputGroup>
          </Box>

          <HStack>
            <Button
              size="sm"
              variant={viewMode === "list" ? "solid" : "outline"}
              onClick={() => setViewMode("list")}
              bgGradient={viewMode === "list" ? "linear-gradient(135deg, #38BBD1, #3EEAEC)" : undefined}
              color={viewMode === "list" ? "white" : textColor}
              borderColor={borderColor}
              _hover={viewMode === "list" ? {
                bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                opacity: 0.9,
              } : {
                bg: menuHoverBg,
              }}
            >
              List
            </Button>
            <Button
              size="sm"
              variant={viewMode === "daily" ? "solid" : "outline"}
              onClick={() => setViewMode("daily")}
              bgGradient={viewMode === "daily" ? "linear-gradient(135deg, #38BBD1, #3EEAEC)" : undefined}
              color={viewMode === "daily" ? "white" : textColor}
              borderColor={borderColor}
              _hover={viewMode === "daily" ? {
                bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                opacity: 0.9,
              } : {
                bg: menuHoverBg,
              }}
            >
              Daily
            </Button>
            <Button
              size="sm"
              variant={viewMode === "weekly" ? "solid" : "outline"}
              onClick={() => setViewMode("weekly")}
              bgGradient={viewMode === "weekly" ? "linear-gradient(135deg, #38BBD1, #3EEAEC)" : undefined}
              color={viewMode === "weekly" ? "white" : textColor}
              borderColor={borderColor}
              _hover={viewMode === "weekly" ? {
                bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                opacity: 0.9,
              } : {
                bg: menuHoverBg,
              }}
            >
              Weekly
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Entries */}
      {filteredEntries.length === 0 ? (
        <Box textAlign="center" py={12} bg={bgColor} borderRadius="md">
          <Text fontSize="lg" color={textColor} mb={2}>
            No journal entries found
          </Text>
          <Text color={useColorModeValue("gray.400", "white")} mb={4}>
            {filters.searchQuery || filters.type !== "all"
              ? "Try adjusting your filters"
              : "Start by creating your first journal entry"}
          </Text>
          {!filters.searchQuery && filters.type === "all" && (
            <Button
              bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
              color="white"
              onClick={handleCreateEntry}
              _hover={{
                bgGradient: "linear-gradient(135deg, #3EEAEC, #38BBD1)",
              }}
            >
              Create First Entry
            </Button>
          )}
        </Box>
      ) : viewMode === "list" ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {filteredEntries.map((entry) => (
            <JournalEntryCard
              key={entry.id}
              entry={entry}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
            />
          ))}
        </SimpleGrid>
      ) : (
        <VStack align="stretch" spacing={6}>
          {groupedEntries &&
            Array.from(groupedEntries.entries())
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([dateKey, dateEntries]) => (
                <Box key={dateKey} bg={bgColor} borderRadius="md" p={4}>
                  <Text fontSize="lg" fontWeight="bold" color="#38BBD1" mb={4}>
                    {viewMode === "daily"
                      ? formatDate(dateKey)
                      : `Week of ${formatWeekRange(dateKey)}`}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {dateEntries.map((entry) => (
                      <JournalEntryCard
                        key={entry.id}
                        entry={entry}
                        onEdit={handleEditEntry}
                        onDelete={handleDeleteEntry}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              ))}
        </VStack>
      )}

      {/* Journal Entry Modal */}
      <JournalEntryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
        onSave={handleSaveEntry}
      />
    </Flex>
  );
};

export default Journal;

